import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { UploadCloud, Printer, Loader2, X, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// BulkTagImport — modal that accepts a CSV blob (paste or file), calls the
// server to persist every valid row, and then offers a printable A4 sheet of
// QR tags (4 columns × 6 rows = 24 tags/page).
// -----------------------------------------------------------------------------
const SAMPLE_CSV = `item_code,metal,purity,gross_weight,net_weight,gold_weight,unit,quantity,cost_per_unit,making_pct,gst_pct,hallmark
IT LR118,Gold,22K,1.950,1.950,3.453,g,1,7000,10,3,HUID-1234
IT NK220,Gold,22K,12.500,12.100,12.100,g,1,7100,12,3,HUID-5678
IT ER300,Silver,925 Silver,4.200,4.200,4.200,g,2,90,8,3,`;

export default function BulkTagImport({ open, onClose, onImported }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState([]);
  const [errors, setErrors] = useState([]);
  const fileRef = useRef(null);

  if (!open) return null;

  const pickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("CSV too large (2 MB max)"); return; }
    const t = await f.text();
    setText(t);
  };

  const runImport = async () => {
    if (!text.trim()) { toast.error("Paste or upload a CSV first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/erp/tags/bulk-import", { csv_text: text });
      setCreated(r.data.created || []);
      setErrors(r.data.errors || []);
      const okN = r.data.counts?.ok || 0;
      const failN = r.data.counts?.failed || 0;
      toast.success(`Imported ${okN} tag${okN !== 1 ? "s" : ""}${failN ? ` · ${failN} row(s) skipped` : ""}`);
      onImported?.(r.data.created || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const printSheet = () => {
    if (created.length === 0) { toast.error("Nothing to print — run import first"); return; }
    const html = document.getElementById("bulk-tag-sheet")?.innerHTML;
    if (!html) return;
    const w = window.open("", "_blank", "width=900,height=1200");
    if (!w) { toast.error("Popup blocked — allow popups to print."); return; }
    w.document.write(`<!doctype html><html><head><title>Product tag sheet — A4</title>
      <style>
        @page { size: A4; margin: 8mm; }
        body { font-family: system-ui, -apple-system, sans-serif; margin: 0; padding: 8mm; }
        .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4mm; }
        .tag  { border: 1px dashed #999; border-radius: 6px; padding: 6px; page-break-inside: avoid; }
        .row  { display: flex; justify-content: space-between; font-size: 9px; line-height: 12px; }
        .code { font-family: 'JetBrains Mono', monospace; font-weight: 800; font-size: 12px; text-align: center; margin-bottom: 4px; }
        .qr-wrap { display: flex; justify-content: center; margin-bottom: 4px; }
        canvas { image-rendering: pixelated; }
      </style></head><body>${html}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-tag-modal">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="text-emerald-600" size={18} />
            <div className="font-bold text-stone-900">Bulk tag import · CSV → A4 sheet</div>
          </div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="bulk-tag-close"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-3">
          {/* CSV input */}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-1">
                Paste CSV or upload a .csv file
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                placeholder="item_code,metal,purity,gross_weight,net_weight,gold_weight,unit,quantity,cost_per_unit,..."
                className="w-full px-3 py-2 rounded-2xl border border-stone-200 text-xs font-mono outline-none"
                data-testid="bulk-csv-text"
              />
              <div className="flex gap-2 mt-2 flex-wrap">
                <input type="file" accept=".csv,text/csv" ref={fileRef} onChange={onFile} className="hidden" data-testid="bulk-csv-file" />
                <Button type="button" onClick={pickFile} variant="outline" className="rounded-full h-8 text-xs">
                  <UploadCloud size={13} className="mr-1.5" /> Upload .csv
                </Button>
                <Button type="button" onClick={() => setText(SAMPLE_CSV)} variant="ghost" className="rounded-full h-8 text-xs text-stone-600">
                  Load sample
                </Button>
                <Button type="button" onClick={runImport} disabled={busy}
                        className="rounded-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700 ml-auto" data-testid="bulk-import-btn">
                  {busy ? <Loader2 className="animate-spin mr-1" size={13} /> : null} Import & save
                </Button>
              </div>
            </div>

            <div className="rounded-2xl bg-stone-50 border border-stone-200 p-3">
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-1">Required column</div>
              <div className="text-xs text-stone-700 font-mono">item_code</div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mt-3 mb-1">Optional columns</div>
              <div className="text-[11px] text-stone-600 font-mono leading-relaxed break-all">
                metal · purity · gross_weight · net_weight · gold_weight · diamond_weight · stone_weight · unit · quantity · cost_per_unit · making_pct · gst_pct · hallmark · notes
              </div>
              <div className="text-[10px] text-stone-500 mt-3">
                Re-importing an existing item_code updates that row in-place (no duplicates).
              </div>
            </div>
          </div>

          {/* Error list */}
          {errors.length > 0 && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3" data-testid="bulk-errors">
              <div className="text-xs font-black text-rose-800 mb-1">{errors.length} row(s) skipped</div>
              <ul className="text-[11px] text-rose-700 font-mono space-y-0.5">
                {errors.slice(0, 5).map((e, i) => (
                  <li key={i}>row {e.row || "?"} — {e.reason}</li>
                ))}
                {errors.length > 5 && <li>… and {errors.length - 5} more</li>}
              </ul>
            </div>
          )}

          {/* Preview grid */}
          {created.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-stone-800">Preview — {created.length} tag{created.length !== 1 ? "s" : ""} ready to print</div>
                <Button type="button" onClick={printSheet} className="rounded-full h-8 text-xs bg-amber-600 hover:bg-amber-700" data-testid="bulk-print-btn">
                  <Printer size={13} className="mr-1.5" /> Print A4 sheet
                </Button>
              </div>
              <div id="bulk-tag-sheet" className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto bg-white p-2 rounded-xl border border-stone-200">
                {created.map((t) => <BulkTagCell key={t.tag_id} tag={t} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkTagCell({ tag }) {
  const qr = useMemo(() => JSON.stringify({ kind: "f2n_tag", ...tag }), [tag]);
  return (
    <div className="tag rounded-md border border-dashed border-stone-300 p-1.5" data-testid={`bulk-tag-${tag.tag_id}`}>
      <div className="code text-[10px] font-black font-mono text-stone-900 text-center">{tag.item_code} · {tag.purity || tag.metal}</div>
      <div className="qr-wrap flex justify-center my-1"><QRCodeCanvas value={qr} size={70} level="M" /></div>
      <div className="row text-[8px]"><span>Net</span><span className="font-mono">{tag.net_weight ?? "—"}{tag.unit}</span></div>
      <div className="row text-[8px]"><span>Total</span><span className="font-mono">₹{Math.round(tag.totals?.grand_total || 0).toLocaleString("en-IN")}</span></div>
    </div>
  );
}
